import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

import type { QueryConfig } from "@/lib/react-query";
import type { NotificationStatus, NotificationStatusCounts } from "@/types/notification";

type NotificationCountResponse =
  | { count: number }
  | { total: number }
  | { results: number }
  | { delivered: number; read: number; total?: number }
  | number;

export type GetNotificationCountParams = {
  status?: NotificationStatus;
};

export const getNotificationCount = async ({ status }: GetNotificationCountParams = {}) => {
  const response = (await api.get("/carburate/notifications/messages/count/", {
    params: status ? { status } : undefined,
  })) as unknown as NotificationCountResponse;

  if (typeof response === "number") return response;
  if (typeof (response as any)?.count === "number") return (response as any).count;
  if (typeof (response as any)?.total === "number") return (response as any).total;
  if (typeof (response as any)?.results === "number") return (response as any).results;

  // If backend returns both delivered/read, sum them for the "total" query.
  const delivered = (response as any)?.delivered;
  const read = (response as any)?.read;
  if (typeof delivered === "number" && typeof read === "number") {
    const total = (response as any)?.total;
    return typeof total === "number" ? total : delivered + read;
  }

  return 0;
};

export const getNotificationCounts = async (): Promise<NotificationStatusCounts> => {
  const response = (await api.get(
    "/carburate/notifications/messages/count/"
  )) as unknown as NotificationCountResponse;

  if (typeof response === "object" && response !== null) {
    const delivered = (response as { delivered?: number }).delivered;
    const read = (response as { read?: number }).read;

    if (typeof delivered === "number" && typeof read === "number") {
      const total = (response as { total?: number }).total;
      return {
        delivered,
        read,
        total: typeof total === "number" ? total : delivered + read,
      };
    }
  }

  const [totalCount, delivered, read] = await Promise.all([
    getNotificationCount(),
    getNotificationCount({ status: "delivered" }),
    getNotificationCount({ status: "read" }),
  ]);

  if (delivered > 0 || read > 0) {
    return {
      delivered,
      read,
      total: totalCount > 0 ? totalCount : delivered + read,
    };
  }

  if (totalCount > 0) {
    return {
      delivered: totalCount,
      read: 0,
      total: totalCount,
    };
  }

  return {
    delivered: 0,
    read: 0,
    total: 0,
  };
};

export function getNotificationCountByFilter(
  counts: NotificationStatusCounts | undefined,
  filter: "all" | "delivered" | "read",
  fallback?: { total?: number; delivered?: number; read?: number }
): number {
  if (counts) {
    switch (filter) {
      case "delivered":
        return counts.delivered;
      case "read":
        return counts.read;
      default:
        return counts.total;
    }
  }

  if (!fallback) return 0;

  switch (filter) {
    case "delivered":
      return fallback.delivered ?? 0;
    case "read":
      return fallback.read ?? 0;
    default:
      return fallback.total ?? 0;
  }
}

export const getNotificationCountsQueryOptions = () => {
  return queryOptions({
    queryKey: ["notifications-counts"],
    queryFn: () => getNotificationCounts(),
  });
};

export const getNotificationCountQueryOptions = (params: GetNotificationCountParams = {}) => {
  return queryOptions({
    queryKey: ["notifications-count", params],
    queryFn: () => getNotificationCount(params),
  });
};

type UseNotificationCountOptions = GetNotificationCountParams & {
  queryConfig?: QueryConfig<typeof getNotificationCount>;
};

export const useNotificationCount = ({ status, queryConfig }: UseNotificationCountOptions = {}) => {
  return useQuery({
    enabled: hasAccessToken(),
    ...getNotificationCountQueryOptions({ status }),
    refetchOnWindowFocus: true,
    ...queryConfig,
  });
};

type UseNotificationCountsOptions = {
  queryConfig?: QueryConfig<typeof getNotificationCounts>;
};

export const useNotificationCounts = ({ queryConfig }: UseNotificationCountsOptions = {}) => {
  return useQuery({
    enabled: hasAccessToken(),
    ...getNotificationCountsQueryOptions(),
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
    ...queryConfig,
  });
};
